import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Megaphone } from "lucide-react";

interface Banner {
  id: string;
  title: string;
  content: string | null;
}

export function PromotionalBanner() {
  const [banners, setBanners] = useState<Banner[]>([]);

  useEffect(() => {
    const fetchActiveBanners = async () => {
      const { data, error } = await supabase
        .from("promotional_banners")
        .select("id, title, content")
        .eq("is_active", true)
        .or('expires_at.is.null,expires_at.gt.now()')
        .order("created_at", { ascending: false });

      if (!error && data) {
        setBanners(data);
      }
    };

    fetchActiveBanners();

    const channel = supabase
      .channel("promotional-banners")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "promotional_banners",
        },
        () => {
          fetchActiveBanners();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (banners.length === 0) return null;

  // Create marquee content - duplicate banners for seamless loop
  const marqueeContent = [...banners, ...banners];

  return (
    <div className="mb-4 animate-fade-in overflow-hidden bg-primary/5 border border-primary/20 rounded-lg py-3">
      {banners.length === 1 ? (
        <div className="flex items-center justify-center w-full px-4">
          <div className="flex items-center gap-2">
            <Megaphone size={14} className="text-primary shrink-0" />
            <span className="text-sm text-foreground">
              <span className="font-medium">{banners[0].title}</span>
              {banners[0].content && (
                <span className="text-muted-foreground"> — {banners[0].content}</span>
              )}
            </span>
          </div>
        </div>
      ) : (
        <div className="marquee-container group">
          <div className="marquee-content items-center">
            {marqueeContent.map((banner, idx) => (
              <span key={`${banner.id}-${idx}`} className="inline-flex items-center justify-center gap-2 px-8 whitespace-nowrap">
                <Megaphone size={14} className="text-primary shrink-0" />
                <span className="text-sm text-foreground">
                  <span className="font-medium">{banner.title}</span>
                  {banner.content && (
                    <span className="text-muted-foreground"> — {banner.content}</span>
                  )}
                </span>
                <span className="text-primary/40 px-4">✦</span>
              </span>
            ))}
          </div>
          <div className="marquee-content items-center" aria-hidden="true">
            {marqueeContent.map((banner, idx) => (
              <span key={`${banner.id}-dup-${idx}`} className="inline-flex items-center justify-center gap-2 px-8 whitespace-nowrap">
                <Megaphone size={14} className="text-primary shrink-0" />
                <span className="text-sm text-foreground">
                  <span className="font-medium">{banner.title}</span>
                  {banner.content && (
                    <span className="text-muted-foreground"> — {banner.content}</span>
                  )}
                </span>
                <span className="text-primary/40 px-4">✦</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
